import { useApiQuery } from '../../api/useApi';
import axiosClient from '../../api/axios-client';

/**
 * Les préférences de notification, telles que le mobile les LIT — #54, #80.
 *
 * ## Lecture seule, sauf un réglage
 *
 * Le mobile n'édite pas les canaux ni le filtre d'assignation : ils sont de portée
 * **compte**, donc les couper au pouce couperait aussi le bureau. Le téléphone a
 * déjà sa coupure honnête — désabonner *cet appareil* — et c'est la bonne
 * granularité. Seul `notify_show_preview` est éditable ici : ce que l'écran
 * verrouillé expose appartient à celui qui tient l'appareil.
 *
 * ## `effective_reach` vient du SERVEUR
 *
 * L'écran énonce un **effet**, jamais un réglage. Deux options du produit ne font
 * pas ce que leur libellé annonce — `assigned` sans aucune assignation rend un
 * compte totalement muet — et la muité dépend d'une donnée que le mobile n'a pas.
 * Le faire deviner serait un pari qui devient faux le jour où les assignations
 * existent.
 */

export type PorteeEffective = 'all' | 'partial' | 'none';

export type CausePortee =
    | 'assignment_filter_no_assignments'
    | 'all_channels_off'
    | 'some_channels_off'
    | null;

export interface PreferencesNotifications {
    notify_email: boolean;
    notify_sms: boolean;
    notify_whatsapp: boolean;
    notify_live_chat: boolean;
    notify_call: boolean;
    notify_assignment_filter: 'all' | 'assigned' | 'unassigned';
    notify_show_preview: boolean;
    effective_reach: PorteeEffective;
    effective_reach_cause: CausePortee;
    canaux_actifs: string[];
}

export function usePreferencesNotifications() {
    return useApiQuery<{ data: PreferencesNotifications }>('/notification-preferences', {}, {
        // L'écran est un diagnostic : il doit dire l'état d'AUJOURD'HUI, pas celui
        // d'il y a dix minutes. C'est tout l'intérêt d'y venir.
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });
}

/** Le seul réglage éditable au pouce. */
export async function enregistrerApercu(visible: boolean): Promise<void> {
    await axiosClient.put('/notification-preferences', { notify_show_preview: visible });
}

/** La notification de test — le même endpoint que la preuve de #79. */
export async function envoyerNotificationDeTest(): Promise<number> {
    const { data } = await axiosClient.post<{ appareils: number }>('/web-push/welcome');

    return data.appareils;
}

/** Coupe tous les appareils : jetons ET abonnements (#76). */
export async function deconnecterTousLesAppareils(): Promise<void> {
    await axiosClient.post('/logout-all-devices');
}
